import { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import type { OrganizationLocation } from '@/api/organizations/types';
import { useOrganizationLocations } from '@/api/organizations/use-organization-locations';
import useSearchStore from '@/stores/use-search-store';
import Dropdown, { type DropdownOption } from '@/ui/dropdown';
import { Location } from '@/ui/icons/location';

import { SearchBarButton } from '../search-bar-button';

type Props = {
  label?: string;
  style?: any;
};

export default function OrganizationLocationInput({ label, style }: Props) {
  const { theme } = useStyles();
  const { data: locations } = useOrganizationLocations();
  const address = useSearchStore((state) => state.address);
  const setAddress = useSearchStore((state) => state.setAddress);
  const didDefault = useRef(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const options: DropdownOption[] = useMemo(
    () =>
      (locations ?? []).map((loc) => ({
        id: String(loc.id),
        value: String(loc.id),
        title: loc.city,
        caption: loc.address,
      })),
    [locations]
  );

  const selectLocation = (loc: OrganizationLocation) => {
    setAddress({
      description: loc.address || loc.city,
      latitude: loc.latitude,
      longitude: loc.longitude,
    });
  };

  // Default-select the location with the most cars. The backend returns
  // locations sorted by car count desc, so the first entry is the busiest.
  useEffect(() => {
    if (didDefault.current) return;
    if (!locations || locations.length === 0) return;

    didDefault.current = true;

    if (!address) {
      selectLocation(locations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const selectedValues = useMemo(() => {
    if (!locations || !address) return [];
    const match = locations.find(
      (loc) =>
        loc.latitude === address.latitude && loc.longitude === address.longitude
    );
    return match ? [String(match.id)] : [];
  }, [locations, address]);

  return (
    <View>
      {showDropdown ? (
        <Dropdown
          mode="single"
          label={label}
          options={options}
          placeHolderText="Select a location"
          values={selectedValues}
          defaultExpanded
          onSelect={(selected) => {
            const loc = locations?.find(
              (l) => String(l.id) === selected[0]?.value
            );
            if (loc) selectLocation(loc);
          }}
          onClose={() => {
            setShowDropdown(false);
          }}
          style={style}
          optionsContainerStyle={{
            backgroundColor: theme.colors.card.default.backgroundColor,
            position: 'absolute',
            top: 60,
            width: '100%',
          }}
        />
      ) : (
        <SearchBarButton
          onPress={() => {
            if (locations && locations.length > 0) {
              setShowDropdown(true);
            }
          }}
          icon={<Location />}
          title="Location"
          caption={address?.description || undefined}
          style={{
            width: '100%',
            border: 'none',
          }}
          active={showDropdown}
        />
      )}
    </View>
  );
}
